import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, User as UserIcon, Loader2 } from "lucide-react";
import { usersApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function UsersAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setUsers(await usersApi.list());
    } catch (e) {
      toast.error("Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const val = email.trim().toLowerCase();
    if (!val) return;
    setAdding(true);
    try {
      await usersApi.add(val, role, name.trim());
      setEmail("");
      setName("");
      setRole("user");
      toast.success("Kullanıcı eklendi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eklenemedi");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.name ? u.name + " — " : ""}${u.email} yetkisi kaldırılsın mı?`)) return;
    try {
      await usersApi.remove(u.id);
      toast.success("Kullanıcı silindi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 md:p-10" data-testid="users-admin-page">
      <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">Kullanıcılar</h1>
      <p className="text-sm text-slate-500 mt-2 mb-8">
        Yalnızca bu listedeki e-postalar giriş yapabilir. Aynı e-posta farklı isimlerle birden çok eklenebilir (görev atamaları için ayrı kişiler). Yöneticiler kullanıcı ekleyip silebilir.
      </p>

      <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm mb-8">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">E-posta</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            data-testid="new-user-email" placeholder="ornek@naswood.com.tr"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">İsim</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="new-user-name" placeholder="Ad Soyad"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Rol</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="new-user-role"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500 bg-white">
            <option value="user">Kullanıcı</option>
            <option value="admin">Yönetici</option>
          </select>
        </div>
        <button type="submit" disabled={adding} data-testid="add-user-btn"
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors disabled:opacity-60">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Ekle
        </button>
      </form>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor…
          </div>
        ) : (
          <table className="w-full text-left border-collapse" data-testid="users-table">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-4 py-3 font-semibold">İsim</th>
                <th className="px-4 py-3 font-semibold">E-posta</th>
                <th className="px-4 py-3 font-semibold">Rol</th>
                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.role === "admin" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                      {u.role === "admin" ? <ShieldCheck className="w-3.5 h-3.5" /> : <UserIcon className="w-3.5 h-3.5" />}
                      {u.role === "admin" ? "Yönetici" : "Kullanıcı"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}
                      className="inline-flex items-center gap-1 rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {!users.length && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-slate-400">Kayıt yok</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
